import { useCallback, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { jobGuidManagerFactory } from '@shipstation/async-client-manager';
import { FreightBrowser } from '@shipstation/client-common/components/freight-browser';
import { useFreightQuoteProgress } from '@shipstation/client-common/pusher/hooks/use-freight-quote-progress';
import { NotificationService } from 'ui-components/services/notification-service';
import { PusherService } from '../../../../services/pusher-service';
import Global from '../../../../store/global-selectors';
import { getFreightGridRequest } from '../../../../store/ltl/ltl.actions';
import { FreightQuoteModal } from '../freight-quote-modal/freight-quote-modal';
import { freightBrowserContainerMessages } from './messages';

import type { BaseWeightUnit } from '@shipstation/client-common/types/weight';
import type { DimensionUnit } from '@shipstation/client-common/types/dimensions';

import type { FreightQuoteSeedData } from '../freight-quote-modal/freight-quote-context';
import type { FreightBrowserFormState } from '@shipstation/client-common/components/freight-browser';
import type { LtlAddress, LtlAdditionalServices, LtlHandlingUnit, LtlQuote } from '@shipstation/client-common/types/ltl';

type ShipFrom = {
  shipFromId: number;
  name: string;
  originAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    countryCode?: string;
    company?: string;
    phone?: string;
    email?: string;
    name?: string;
  };
};

type FreightProvider = {
  sellerFreightProviderId: number | null;
  isConnected: boolean;
};

const DEFAULT_ADDITIONAL_SERVICES: LtlAdditionalServices = {
  isLiftgatePickup: false,
  isInsidePickup: false,
  isResidentialPickup: false,
  isLimitedAccessPickup: false,
  isFlatbedPickup: false,
  isDockLevelPickup: false,
  isTopLoadPickup: false,
  isConstructionSitePickup: false,
  isTradeShowPickup: false,
  isNotifyBeforePickup: false,
  isLiftgateDelivery: false,
  isInsideDelivery: false,
  isResidentialDelivery: false,
  isLimitedAccessDelivery: false,
  isFlatbedDelivery: false,
  isDockLevelDelivery: false,
  isConstructionSiteDelivery: false,
  isTradeShowDelivery: false,
  isAppointmentDelivery: false,
  isCallBeforeDelivery: false,
  isSortAndSegregate: false,
  isNotifyBeforeDelivery: false,
  isHazmat: false,
  isTemperatureControl: false,
  isProtectFromFreezing: false,
  isOverlength8to12ft: false,
  isOverlength12to20ft: false,
  isOverlength20ftPlus: false,
  isGuaranteedDelivery: false,
  isBlindShipment: false,
  isMarkAndTag: false,
  isCOD: false,
};

const INITIAL_FORM_STATE: Omit<FreightBrowserFormState, 'handlingUnits'> = {
  shipFromId: '',
  destination: { city: '', state: '', postalCode: '', countryCode: 'US' },
  additionalServices: DEFAULT_ADDITIONAL_SERVICES,
  insurance: { isInsured: false, conditionOfGoods: '', category: '', insuredValue: 0, marksAndNumbers: '' },
};

type FreightBrowserContainerProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const FreightBrowserContainer = ({ isOpen, onClose }: FreightBrowserContainerProps) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const sellerData = useSelector(Global.selectSellerData);
  const freightProviders = useSelector(Global.selectFreightSettingsProviders);
  const weightUnit = useSelector(Global.selectSellerDefaultWeightUnit) as BaseWeightUnit;
  const dimensionUnit = useSelector(Global.selectSellerDefaultDimensionUnit) as DimensionUnit;

  const createDefaultHandlingUnitWithUnits = useCallback(
    (): LtlHandlingUnit => ({
      id: crypto.randomUUID(),
      packagingType: 'PLT',
      quantity: 1,
      length: 0,
      width: 0,
      height: 0,
      dimensionUnit,
      isStackable: false,
      commodities: [
        {
          id: crypto.randomUUID(),
          description: '',
          freightClass: '',
          nmfcCode: '',
          quantity: 1,
          weight: 0,
          weightUnit,
          isHazmat: false,
          hazmatDetails: null,
          insurance: { isInsured: false, commodityCategoryCode: '', condition: '', declaredValue: 0 },
        },
      ],
    }),
    [dimensionUnit, weightUnit],
  );

  const [formState, setFormState] = useState<FreightBrowserFormState>(() => ({
    ...INITIAL_FORM_STATE,
    handlingUnits: [createDefaultHandlingUnitWithUnits()],
  }));
  const [rates, setRates] = useState<LtlQuote[]>([]);
  const [isLoadingRates, setIsLoadingRates] = useState(false);
  const [rateError, setRateError] = useState<string | undefined>(undefined);
  // When set, the rate browser hands off to the booking wizard seeded with this data.
  const [configureSeed, setConfigureSeed] = useState<FreightQuoteSeedData | null>(null);

  const formStateRef = useRef(formState);
  formStateRef.current = formState;

  const pendingGuidsRef = useRef<Set<string>>(new Set());

  const pusherService = useMemo(
    () => ({
      subscribe: <T,>(
        eventName: string,
        callback: (payload: T) => void,
        fallbackOptions?: { timeoutMs: number; onTimeout?: () => void },
      ) => {
        const manager = jobGuidManagerFactory();
        const unbind = PusherService.bindToEvent(eventName, callback as (message: Record<string, unknown>) => void, undefined, {
          timeoutMs: fallbackOptions?.timeoutMs ?? 30000,
          jobGuidManager: manager,
          resetTimeoutCheck: (msg: object) => pendingGuidsRef.current.has((msg as Record<string, unknown>)?.messageGuid as string),
          completeCheck: (msg: object) => pendingGuidsRef.current.has((msg as Record<string, unknown>)?.messageGuid as string),
          timeoutCompleteCheck: ({ messages }: { messages: object[] }) =>
            messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')),
          timeoutRetryCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
            !messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')) &&
            retryCount < 3,
          timeoutFailureCheck: ({ messages, retryCount }: { messages: object[]; retryCount: number }) =>
            !messages.some((msg) => pendingGuidsRef.current.has(((msg as Record<string, unknown>)?.messageGuid as string) ?? '')) &&
            retryCount >= 3,
          onTimeout: () => fallbackOptions?.onTimeout?.(),
          logContext: {},
        });
        return unbind;
      },
    }),
    [],
  );

  const { startListening, cancelListening } = useFreightQuoteProgress({
    pusherService,
    pendingGuidsRef,
    onQuotes: (quotes) => setRates((prev) => [...prev, ...quotes]),
    onError: (message) => {
      setRateError(message);
      setIsLoadingRates(false);
    },
    onComplete: () => setIsLoadingRates(false),
    timeoutMs: 30000,
  });

  const shipFromOptions = useMemo(() => {
    const shipFroms: ShipFrom[] = sellerData?.shipFroms ?? [];
    return shipFroms.map((sf) => ({ id: String(sf.shipFromId), name: sf.name }));
  }, [sellerData]);

  const getConnectedProviderId = useCallback((): number | undefined => {
    const providers: FreightProvider[] = freightProviders ?? [];
    const connected = providers.find((p) => p.isConnected);
    return connected?.sellerFreightProviderId ?? undefined;
  }, [freightProviders]);

  const resolveOriginAddress = useCallback(
    (shipFromId: string) => {
      const shipFroms: ShipFrom[] = sellerData?.shipFroms ?? [];
      const shipFrom = shipFroms.find((sf) => String(sf.shipFromId) === shipFromId);
      const addr = shipFrom?.originAddress;
      if (!addr) return null;
      return {
        name: addr.name ?? '',
        company: addr.company ?? shipFrom.name ?? '',
        phone: addr.phone ?? '',
        line1: addr.line1 ?? '',
        line2: addr.line2 ?? '',
        city: addr.city ?? '',
        state: addr.state ?? '',
        postalCode: addr.postalCode ?? '',
        countryCode: addr.countryCode ?? 'US',
      };
    },
    [sellerData],
  );

  const handleBrowseRates = useCallback(async () => {
    const current = formStateRef.current;
    const sellerFreightProviderId = getConnectedProviderId();
    if (!sellerFreightProviderId) {
      setRateError(intl.formatMessage(freightBrowserContainerMessages.noProviderConnected));
      return;
    }

    setIsLoadingRates(true);
    setRateError(undefined);
    setRates([]);
    cancelListening();

    const originAddress = resolveOriginAddress(current.shipFromId);
    const services = current.additionalServices;

    const payload = {
      sellerFreightProviderId,
      shipment: {
        shipFromId: current.shipFromId ? Number(current.shipFromId) : null,
        originAddress: originAddress ?? {
          city: '',
          state: '',
          postalCode: '',
          countryCode: 'US',
        },
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        shipmentDate: `${new Date().toISOString().split('T')[0]}T09:00:00`,
        handlingUnits: current.handlingUnits.map((unit) => ({
          packagingType: unit.packagingType,
          quantity: unit.quantity,
          length: unit.length,
          width: unit.width,
          height: unit.height,
          dimensionUnit: unit.dimensionUnit ?? dimensionUnit,
          isStackable: unit.isStackable,
          commodities: unit.commodities.map((c) => ({
            freightClass: c.freightClass || null,
            description: c.description || null,
            quantity: c.quantity,
            weight: c.weight,
            weightUnit: c.weightUnit ?? weightUnit,
            nmfcCode: c.nmfcCode || null,
            isHazmat: c.isHazmat,
          })),
        })),
        additionalServices: {
          liftgatePickup: services.isLiftgatePickup,
          liftgateDelivery: services.isLiftgateDelivery,
          insidePickup: services.isInsidePickup,
          insideDelivery: services.isInsideDelivery,
          residentialPickup: services.isResidentialPickup,
          residentialDelivery: services.isResidentialDelivery,
          appointmentDelivery: services.isAppointmentDelivery,
          notifyBeforeDelivery: services.isCallBeforeDelivery,
          sortAndSegregate: services.isSortAndSegregate,
          protectionFromCold: services.isProtectFromFreezing,
          holdAtTerminal: false,
        },
        insurance: current.insurance.isInsured
          ? {
              requestInsurance: true,
              declaredValue: current.insurance.insuredValue,
              commodityCategory: current.insurance.category || null,
              itemCondition: current.insurance.conditionOfGoods || null,
            }
          : null,
      },
    };

    try {
      const data = await axios.post('/freight/quote', payload).then((r) => r.data);
      if (data.success === false) {
        setRateError(data.errors?.[0]?.message || intl.formatMessage(freightBrowserContainerMessages.failedToRetrieveQuotes));
        setIsLoadingRates(false);
        return;
      }
      const jobGuids: string[] = (data.jobGuids ?? data.JobGuids ?? []) as string[];
      if (jobGuids.length === 0) {
        setIsLoadingRates(false);
        return;
      }
      startListening(jobGuids);
    } catch (err) {
      const responseData = (err as { response?: { data?: unknown } })?.response?.data;
      const message =
        (typeof responseData === 'string' ? responseData : null) ??
        (responseData as { Message?: string })?.Message ??
        (err instanceof Error ? err.message : null) ??
        intl.formatMessage(freightBrowserContainerMessages.rateFetchError);
      setRateError(message);
      setIsLoadingRates(false);
    }
  }, [cancelListening, dimensionUnit, getConnectedProviderId, intl, resolveOriginAddress, startListening, weightUnit]);

  const handleSaveQuote = useCallback(
    async (selectedQuote: LtlQuote | null) => {
      const current = formStateRef.current;
      const sellerFreightProviderId = getConnectedProviderId();
      if (!sellerFreightProviderId) return;

      const originAddress = resolveOriginAddress(current.shipFromId);

      const totalWeight = current.handlingUnits.reduce(
        (sum, unit) => sum + unit.commodities.reduce((cSum, c) => cSum + c.weight * c.quantity, 0) * unit.quantity,
        0,
      );

      const payload = {
        sellerFreightProviderId,
        shipFromWarehouseId: current.shipFromId ? Number(current.shipFromId) : null,
        originAddress: originAddress ?? null,
        destinationAddress: {
          city: current.destination.city,
          state: current.destination.state,
          postalCode: current.destination.postalCode,
          countryCode: current.destination.countryCode,
        },
        handlingUnits: current.handlingUnits.map((unit) => ({
          packagingType: unit.packagingType,
          quantity: unit.quantity,
          length: unit.length,
          width: unit.width,
          height: unit.height,
          dimensionUnit: unit.dimensionUnit ?? dimensionUnit,
          isStackable: unit.isStackable,
          commodities: unit.commodities.map((c) => ({
            freightClass: c.freightClass || null,
            quantity: c.quantity,
            weight: c.weight,
            weightUnit: c.weightUnit ?? weightUnit,
            isHazmat: c.isHazmat,
          })),
        })),
        allQuotes: rates.map((q) => ({
          offerId: q.offerId,
          productTransactionId: q.productTransactionId,
          carrierName: q.carrierName,
          carrierCode: q.carrierCode,
          serviceName: q.serviceName,
          totalCharges: q.totalCharges,
          transitDays: q.transitDays,
          estimatedDeliveryDate: q.estimatedDeliveryDate,
          isGuaranteed: q.isGuaranteed,
        })),
        shipDate: new Date().toISOString().split('T')[0],
        totalWeight,
        ...(selectedQuote && {
          offerId: selectedQuote.offerId,
          productTransactionId: selectedQuote.productTransactionId,
          carrierName: selectedQuote.carrierName,
          carrierScac: selectedQuote.carrierCode,
          serviceName: selectedQuote.serviceName,
          totalCharges: selectedQuote.totalCharges,
          transitDays: selectedQuote.transitDays,
          estimatedDeliveryDate: selectedQuote.estimatedDeliveryDate,
        }),
      };

      try {
        await axios.post('/freight/save-quote', payload);
        NotificationService.showGenericSuccessNotification(
          undefined,
          <FormattedMessage {...freightBrowserContainerMessages.saveQuoteSuccess} />,
        );
        dispatch(getFreightGridRequest());
      } catch (err) {
        const errorMessage =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          intl.formatMessage(freightBrowserContainerMessages.saveQuoteFailed);
        NotificationService.showGenericFailureNotification(undefined, errorMessage);
      }
    },
    [dimensionUnit, dispatch, getConnectedProviderId, intl, rates, resolveOriginAddress, weightUnit],
  );

  const handleConfigureShipment = useCallback(
    (selectedQuote: LtlQuote) => {
      const current = formStateRef.current;
      const destinationAddress: LtlAddress = {
        name: '',
        company: '',
        line1: '',
        line2: '',
        city: current.destination.city,
        state: current.destination.state,
        postalCode: current.destination.postalCode,
        countryCode: current.destination.countryCode || 'US',
        phone: '',
        email: '',
        locationType: '',
      };

      // Hand off the in-memory browser form + fetched rates to the booking wizard.
      // The wizard resolves the origin address itself from selectedShipFromId.
      setConfigureSeed({
        selectedShipFromId: current.shipFromId,
        destinationAddress,
        handlingUnits: current.handlingUnits,
        additionalServices: current.additionalServices,
        quotes: rates,
        selectedQuote,
      });
    },
    [rates],
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  if (configureSeed) {
    return <FreightQuoteModal onClose={handleClose} seed={configureSeed} />;
  }

  return (
    <FreightBrowser
      formState={formState}
      isLoadingRates={isLoadingRates}
      isOpen={isOpen}
      onBrowseRates={handleBrowseRates}
      onClose={handleClose}
      onConfigureShipment={handleConfigureShipment}
      onFormChange={setFormState}
      onSaveQuote={handleSaveQuote}
      rateError={rateError}
      rates={rates}
      shipFromOptions={shipFromOptions}
    />
  );
};
